import * as React from "react";
import { format, parse } from "date-fns";
import { es } from "react-day-picker/locale";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const ISO_DATE = "yyyy-MM-dd";
const DISPLAY_FORMAT = "dd/MM/yyyy";

function parseIsoDate(value: string): Date | undefined {
  if (!value?.trim()) return undefined;
  try {
    const d = parse(value.trim(), ISO_DATE, new Date());
    return isNaN(d.getTime()) ? undefined : d;
  } catch {
    return undefined;
  }
}

export interface DatePickerFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  label: React.ReactNode;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Trigger button class name */
  triggerClassName?: string;
  /** When "inline", label is shown to the left of the input in one row; default "top" stacks label above */
  labelPosition?: "top" | "inline";
}

export function DatePickerField({
  id,
  value,
  onChange,
  label,
  error,
  disabled,
  placeholder = "dd/mm/aaaa",
  className,
  triggerClassName,
  labelPosition = "top",
}: DatePickerFieldProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const selectedDate = parseIsoDate(value);

  const handleSelect = React.useCallback(
    (date: Date | undefined) => {
      if (!date) return;
      onChange(format(date, ISO_DATE));
      setOpen(false);
    },
    [onChange]
  );

  const displayValue = selectedDate
    ? format(selectedDate, DISPLAY_FORMAT, { locale: es })
    : "";

  const isInline = labelPosition === "inline";

  return (
    <div
      className={cn(
        isInline ? "flex flex-row items-center gap-2" : "space-y-2",
        className
      )}
    >
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "text-sm font-medium leading-none",
            isInline && "shrink-0"
          )}
        >
          {label}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-start text-left font-normal",
              !displayValue && "text-muted-foreground",
              error && "border-destructive",
              triggerClassName
            )}
          >
            <CalendarIcon className="mr-2 size-4" />
            {displayValue || placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 z-[100]"
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={12}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            locale={es}
          />
        </PopoverContent>
      </Popover>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}
